import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import type {
  AdminAuthenticatedSession,
  AdminLoginRequest,
  AdminLoginResponseData,
  AdminSummary,
} from "@chaochud/contracts";

import { adminAuthClient } from "../api-clients";
import {
  clearAdminSession,
  getAdminAccessToken,
  getAdminRefreshToken,
  hydrateAdminFromStorage,
  saveAdminSession,
} from "./token-storage";

interface AdminAuthContextValue {
  admin: AdminSummary | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  login(payload: AdminLoginRequest): Promise<AdminLoginResponseData>;
  completeSession(session: AdminAuthenticatedSession): void;
  logout(): Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [admin, setAdmin] = useState<AdminSummary | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    const state = hydrateAdminFromStorage();
    setAdmin(state.admin);
    setAccessToken(state.accessToken);
    setIsHydrated(true);
  }, []);

  const completeSession = useCallback((session: AdminAuthenticatedSession) => {
    saveAdminSession(session.accessToken, session.refreshToken, session.admin);
    setAdmin(session.admin);
    setAccessToken(session.accessToken);
  }, []);

  const login = useCallback(async (payload: AdminLoginRequest) => {
    const result = await adminAuthClient.login(payload);
    if (result.status === "AUTHENTICATED") {
      completeSession(result);
    }
    return result;
  }, [completeSession]);

  const logout = useCallback(async () => {
    const refreshToken = getAdminRefreshToken();
    if (refreshToken) {
      try {
        await adminAuthClient.logout({ refreshToken });
      } catch {
        // Best-effort local clear.
      }
    }
    clearAdminSession();
    setAdmin(null);
    setAccessToken(null);
  }, []);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      admin,
      isAuthenticated: Boolean(accessToken ?? getAdminAccessToken()),
      isHydrated,
      login,
      completeSession,
      logout,
    }),
    [admin, accessToken, isHydrated, login, completeSession, logout],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthContextValue {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  }
  return context;
}
